<template>
  <GenericSideMenu
    position="right"
    class="MiniCart overflow-y-auto"
    content-class="!p-0"
    :title="`${$t('minicart.headline')}`"
    :title-meta="cartItems"
    :title-description="titleDescription"
    :with-extension="!uiStore.isMobile && relatedProducts.length > 0"
    @close="close"
  >
    <template v-if="globalContent.getIsB2b" #extended>
      <div class="w-full">
        <div
          class="type-headline-lg p-12"
        >
          {{ $t('accessories.headline') }}
        </div>
        <client-only>
          <div
            v-for="(product, i) in relatedProducts"
            :key="product.partNo"
            class="px-12 pb-24"
          >
            <ProductCard
              :product="product"
              :enable-quick-buy="true"
              :placement="i"
              :design="'miniCart'"
            />
          </div>
        </client-only>
      </div>
    </template>

    <CheckoutFreeShippingProgressBar
      v-if="cartStore.items.length && globalContent.getIsB2b && globalContent.getEnableFreeShippingProgress"
      class="px-12 pt-12 desk:px-24"
    />

    <div
      v-if="cartStore.items.length"
      class="p-12 desk:p-24"
      :class="{
        'pt-24': orderProposalId,
      }"
    >
      <MiniCartProductV2
        v-for="(item) in visibleCartItems"
        :key="item.lineNo"
        :item="item"
        :hide-cross-docking="true"
        :disable-interaction="false"
      />
      <div
        class="flex flex-col gap-12 desk:gap-16 py-12 desk:pb-16 desk:pt-12 type-xs desk:type-sm leading-single"
        :class="{
          'opacity-50': cartStore.isLoading
        }"
      >
        <div v-if="showPoints && isCheckout" class="flex justify-between">
          <div class="flex items-center">
            <div>
              {{ $t('minicart.summary.points') }}
            </div>
            <Tooltip class="ml-6" :text="$t('minicart.summary.pointsInfo', {amount: globalContent.getPointCollectingThreshold})">
              <img
                class="w-12 h-12 desk:w-16 desk:h-16"
                src="/icons/info.svg"
                alt=""
              >
            </Tooltip>
          </div>
          <div class="pl-16 font-medium text-right">
            {{ `${cartStore.cart.summary.points} ${$t('minicart.summary.pointsSuffix')}` }}
          </div>
        </div>
        <div v-if="showPoints && isCheckout" class="color-light h-1 w-full" />
        <div
          v-for="fee in cartStore.cart.summary?.additionalFees"
          :key="fee.name"
          class="flex justify-between"
        >
          <div>{{ fee.name }}</div>
          <div class="pl-16 font-medium text-right">
            {{ fee.feeBeforeVatDisplay }}
          </div>
        </div>
        <div v-if="isCheckout" class="flex justify-between">
          <div>{{ $t('minicart.summary.sum') }}</div>
          <div class="pl-16 font-medium text-right">
            {{ cartStore.cart.summary?.subtotalBeforeVatDisplay }}
          </div>
        </div>
        <div v-if="isCheckout" class="flex justify-between">
          <div>{{ $t('minicart.summary.freight') }}</div>
          <div class="pl-16 font-medium text-right">
            <span>{{ cartStore.cart.summary?.shippingBeforeVatDisplay }}</span>
          </div>
        </div>
        <div
          v-for="extraFee in deliveryExtraFeeItems"
          :key="extraFee.lineNo"
          class="flex justify-between"
        >
          <div>{{ extraFee.variantName || extraFee.name }}</div>
          <div class="pl-16 font-medium text-right">
            {{ extraFee.priceInfoTotal?.priceBeforeVatDisplay || extraFee.priceInfo?.priceBeforeVatDisplay }}
          </div>
        </div>
        <div v-if="cartStore.cart.summary?.crossDockingShippingBeforeVat && isCheckout" class="flex justify-between">
          <div>{{ $t('crossDocking.shipping') }}</div>
          <div class="pl-16 text-right">
            <span>{{ cartStore.cart.summary?.crossDockingShippingBeforeVatDisplay }}</span>
          </div>
        </div>
        <div v-if="cartStore.cart.summary.vatDisplay && isCheckout" class="flex justify-between">
          <div>{{ $t('minicart.summary.vat') }}</div>
          <div class="pl-16 font-medium text-right">
            ({{ cartStore.cart.summary.vatDisplay.trim() }})
          </div>
        </div>
        <div
          v-if="cartStore.hasDiscount"
          class="flex justify-between items-center"
        >
          <div>{{ $t('minicart.summary.discount') }}</div>
          <div
            class="pl-16 text-right text-criticalDark"
            :class="{
              'type-headline-sm text-text-errorStrong': !isCheckout
            }"
          >
            -{{ cartStore.cart.summary.discountDisplay.trim() }}
          </div>
        </div>
        <div v-if="isCheckout || (cartStore.hasDiscount && !isCheckout)" class="bg-border-subtle h-1 w-full" />
        <div>
          <div class="flex justify-between items-end mb-8 type-headline-sm leading-single">
            <div>{{ $t('minicart.summary.incVat') }}</div>
            <div class="flex items-center gap-8">
              <div
                v-if="cartStore.cart.summary.priceBeforeVatAndDiscount && cartStore.cart.summary.priceBeforeVatAndDiscount !== cartStore.cart.summary.priceBeforeVat"
                class="type-base text-text-subtle line-through"
              >
                {{ cartStore.cart.summary.priceBeforeDiscountDisplay }}
              </div>
              <div
                class="leading-single type-headline-md text-text-base"
                :class="{ '!text-text-errorStrong': cartStore.cart.summary.priceBeforeVatAndDiscount }"
              >
                {{ cartStore.cart.summary.priceDisplay }}
              </div>
            </div>
          </div>
          <div class="flex justify-between items-end text-text-subtle">
            <div class="capitalize">({{ $t('price.incVat.short') }})</div>
            <div class="pl-16 text-right">({{ cartStore.cart.summary.vatDisplay.trim() }})</div>
          </div>
        </div>
        <nuxt-link
          :to="manualUrl('/checkout')"
          class="btn btn--lg w-full"
          :class="{
            'loading': cartStore.isLoading
          }"
        >
          {{ $t('minicart.toCheckout') }}
        </nuxt-link>

        <div v-if="globalContent.getIsB2b" class="flex items-center justify-center gap-6 type-sm">
          <img
            src="/icons/truck.svg"
            class="text-darkest w-20 h-20"
            alt="delivery"
          >
          {{ $t('miniCart.shipping.calculatedInCheckout') }}
        </div>
      </div>

      <div v-if="globalContent.getIsB2b" class="desk:hidden">
        <div v-if="relatedProducts.length" class="type-headline-lg leading-single flex items-start my-12">
          {{ $t('accessories.headline') }}
        </div>
        <client-only>
          <Swiper
            :key="`swiperForTab${swiperIndex}`"
            :space-between="12"
            :slides-per-view="2.2"
            :slides-offset-before="0"
            :slides-offset-after="24"
            :modules="[SwiperNavigation, SwiperPagination]"
            :navigation="{
              nextEl: `.swiper-button-next.a123`,
              prevEl: `.swiper-button-prev.a123`,
            }"
            :pagination="{
              dynamicBullets: true,
              clickable: true,
            }"
            @resize="swiper => { updateSwiperH(swiper.height) }"
          >
            <template
              v-for="(product, i) in relatedProducts"
              :key="product.partNo"
            >
              <SwiperSlide>
                <ProductCard
                  :product="product"
                  :enable-quick-buy="true"
                  :placement="i"
                  :design="'miniCart'"
                />
              </SwiperSlide>
            </template>
          </Swiper>
        </client-only>
      </div>
    </div>
    <div v-else class="flex flex-col items-center justify-center p-24 h-full w-full relative pb-88 type-sm">
      {{ $t('minicart.empty') }}
    </div>
  </GenericSideMenu>
</template>

<script setup lang="ts">
import GenericSideMenu from '~/components/menu/GenericSideMenu.vue';
import { useCartStore } from '~/store/cart';
import { storeToRefs } from 'pinia';
import { useUiStore } from '~/store/ui';
import { createEventFromCart } from '~/composeables/useTracking';
import useLinkReplacer from '~/composeables/useLinkReplacer';
import { useGlobalContentStore } from '~/store/globalContent';
import Tooltip from '~/components/body/Tooltip.vue';
import { useUserStore } from '~/store/user';
import CheckoutFreeShippingProgressBar from '~/components/checkout/CheckoutFreeShippingProgressBar.vue';
import { NorceRelatedProductTypeCodes } from '~/constants/norceCodes';
import { CartApiItem } from '~/constants/types/norce';
import useAlgoliaFetch from '~/composeables/useAlgoliaFetch';
import ProductCard from '~/components/product/ProductCard.vue';
import Product, { ProductModel } from '~/models/product';
import useLocalDate from '~/composeables/useLocalDate';
import MiniCartProductV2 from '~/components/cart/MiniCartProductV2.vue';

const { getLocaleDate } = useLocalDate();
const { getExactVariantByPartNo } = useAlgoliaFetch();
const cartStore = useCartStore();
const userStore = useUserStore();
const { cartItems } = storeToRefs(cartStore);
const { $toNumber } = useNuxtApp();
const uiStore = useUiStore();
const globalContent = useGlobalContentStore();

const swiperIndex = ref(0);
const swiperH = ref(-1);
const updateSwiperH = (h: number) => {
  if (h < 500) {
    swiperH.value = h;
  }
};

const { manualUrl } = useLinkReplacer();

const route = useRoute();
const close = () => {
  uiStore.setShowMiniCart(false);
};

const emit = defineEmits(['closeMenu']);

onMounted(async() => {
  //await loadRelatedProducts();
  createEventFromCart('view_cart');
});

const titleDescription = computed(() => {
  if (userStore.isSalesRepUser && cartStore.cart.norceBasketId) {
    return `#${ cartStore.cart.norceBasketId }`;
  }
  return '';
});

const clearOrderProposalForSalesRep = async() => {
  cartStore.deleteCart();
  uiStore.closeAllModal();
  emit('closeMenu');
  await userStore.setCurrentlyEditingOrderProposal(null);
};

const clearOrderProposalForCustomer = async() => {
  cartStore.deleteCart();
  uiStore.closeAllModal();
};

const hiddenPartNos = computed(() => {
  return [
    globalContent.consumerFeePartNo,
    globalContent.outsideModDeliveryFeePartNo,
    globalContent.tailLiftPartNo,
    globalContent.timedUnloadingPartNo,
  ].filter((partNo): partNo is string => Boolean(partNo));
});

const visibleCartItems = computed(() => {
  return cartStore.items.filter(item => !hiddenPartNos.value.includes(item.partNo));
});

const deliveryExtraFeeItems = computed(() => {
  const feePartNos = [
    globalContent.outsideModDeliveryFeePartNo,
    globalContent.tailLiftPartNo,
    globalContent.timedUnloadingPartNo,
  ].filter((partNo): partNo is string => Boolean(partNo));

  return cartStore.items.filter(item => feePartNos.includes(item.partNo));
});

const totalPrice = computed(() => {
  return $toNumber(cartStore.cart.summary.priceBeforeVat) - $toNumber(cartStore.cart.summary.shippingBeforeVat);
});

const isOrderProposalBasket = computed(() => {
  return !!(userStore.isEditingOrderProposal ? userStore.userProfile.currentlyEditingOrderProposal?.norceBasketId : null) || cartStore.isOrderProposalBasket;
});

const orderProposalId = computed(() => {
  return userStore.isEditingOrderProposal ? userStore.userProfile.currentlyEditingOrderProposal?.norceBasketId : null;
});

const copiedFromOrderId = computed(() => {
  if (userStore.isSalesRepUser || userStore.isCustomerSuccessUser) {
    return cartStore.getCopiedFromOrderId;
  }
  return null;
});

const showPoints = computed(() => {
  return userStore.allowPointsPurchase && globalContent.getPointCollectingThreshold <= totalPrice.value;
});

const isBackorderBasket = computed(() => {
  return cartStore.cart.items.some((item) => item.stockStatus.value < 1);
});

const isCheckout = computed(()=> {
  return route.name?.toString()?.endsWith('checkout');
});

const relationTypes = [
  NorceRelatedProductTypeCodes.Mouldings,
  NorceRelatedProductTypeCodes.Skirtings,
  NorceRelatedProductTypeCodes.Profiles,
  NorceRelatedProductTypeCodes.Aftercare,
  NorceRelatedProductTypeCodes.Installation,
];

const relatedProducts = ref<ProductModel[]>([]);

const cartPartNos = computed(() => {
  return cartStore.cart?.items?.map((i: CartApiItem) => i.partNo);
});
const loadRelatedProducts = async() => {
  const allPartNos = [] as string[];
  if (cartPartNos.value.length) {
    const res = await getExactVariantByPartNo(cartPartNos.value);
    if (res?.hits) {
      res.hits.map((p) => {
        p.relatedProducts?.filter(f => relationTypes.includes(f.code)).map((r) => allPartNos.push(r.partNo));
      });
    }

    const uniquePartNos = [...new Set(allPartNos)];
    if (!uniquePartNos.length) {
      return;
    }

    const products = await getExactVariantByPartNo(uniquePartNos);
    if (products) {
      relatedProducts.value = products.hits?.map((p) => Product.create(p, true)).slice(0, 8);
    }
  }
};

const showRequestProposal = ref(false);

const showSavedCartMessage = computed(() => {
  return (userStore.isCustomerSuccessUser || userStore.isSalesRepUser) && (cartStore.cart.savedBasket && !cartStore.cart.savedBasket?.createdByMe);
});

</script>

<style scoped lang="postcss">
</style>
